<template>
  <div class="no-results" data-name="no-results" :class="sizes.section">
    <div v-if="image" class="no-results__image-wrapper" :style="{ height: staticImageSize }">
      <img class="no-results__image" alt="" :src="image" />
    </div>

    <slot name="sup" />

    <h6 v-if="headerText" class="no-results__header" :class="sizes.title">
      {{ headerText }}
    </h6>

    <p v-if="text" class="no-results__text" :class="sizes.text">{{ text }}<slot name="right-text-icon" /></p>

    <div v-if="buttonText" class="no-results__action">
      <router-link v-if="to" v-slot="{ navigate, href }" :to="to" custom data-name="no-results-action">
        <ol-button :label="buttonText" class="no-results__btn" :size="sizes.btn" :href="href" @click="navigate" />
      </router-link>

      <ol-button
        v-else
        :label="buttonText"
        class="no-results__btn"
        :size="sizes.btn"
        data-name="no-results-action"
        @click="emit('button-click')"
      />
    </div>

    <div v-if="$slots.footer" class="no-results__footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup>
import { computed, inject } from 'vue'
import { OlButton } from '@gamesite/game-core'

const props = defineProps({
  text: {
    type: String,
    default: '',
  },
  image: {
    type: String,
    default: '',
  },
  buttonText: {
    type: String,
    default: '',
  },
  headerText: {
    type: String,
    default: '',
  },
  to: {
    type: Object,
    default: () => null,
  },
  staticSize: {
    type: String,
    default: '',
    validate: (e) => ['', 'xs', 'sm', 'md', 'lg'].includes(e),
  },
  staticImageSize: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['button-click'])
const mediaQueries = inject('mediaQueries')

const sizes = computed(() => {
  let size = props.staticSize

  if (!size) {
    if (mediaQueries.desktop) {
      size = 'lg'
    } else if (mediaQueries.tab) {
      size = 'md'
    } else {
      size = 'sm'
    }
  }

  const map = {
    lg: {
      title: 'text-header-3xl',
      text: 'text-body-lg',
      section: 'no-results--lg',
      btn: 'md',
    },
    md: {
      title: 'text-header-3xl',
      text: 'text-body-lg',
      section: 'no-results--md',
      btn: 'md',
    },
    sm: {
      title: 'text-header-md',
      text: 'text-body-sm',
      section: 'no-results--sm',
      btn: 'sm',
    },
  }

  return map[size] ?? map.sm
})
</script>

<style scoped lang="scss">
.no-results {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 24px;

  &--lg {
    min-height: 382px;

    .no-results__image-wrapper {
      height: 200px;
    }

    .no-results__header {
      margin-top: 16px;
    }

    .no-results__text {
      margin-top: 8px;
    }

    .no-results__btn {
      margin-top: 16px;
    }
  }
  &--md {
    min-height: 320px;

    .no-results__image-wrapper {
      height: 200px;
    }

    .no-results__header {
      margin-top: 16px;
    }

    .no-results__text {
      margin-top: 8px;
    }

    .no-results__btn {
      margin-top: 16px;
    }
  }
  &--sm {
    min-height: 196px;

    .no-results__image-wrapper {
      height: 160px;
    }

    .no-results__header {
      margin-top: 16px;
    }

    .no-results__text {
      margin-top: 8px;
    }

    .no-results__btn {
      margin-top: 8px;
    }
  }

  &__image {
    height: 100%;
    display: block;
    object-fit: contain;
  }

  &__header {
    color: var(--color-text-primary);
    text-align: center;
  }

  &__text {
    color: var(--color-text-secondary);
    text-align: center;
  }

  &__action + &__footer {
    margin-top: 40px;
  }
}
</style>
